import React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import type { Citation } from './types';
import {
  isGsarCitation,
  isLegacyFarCitation,
  SUPPLEMENT_538_71_LABEL,
} from './citationAuthority';

/**
 * Tokenize a quote string into a sequence of "blocks" that the renderer
 * can map to either a single indented `<Box>` (regular line) or a real
 * `<table>` element (markdown table).
 *
 * A markdown table is detected as 2+ consecutive lines whose lstrip
 * starts with `|`. The second such line is treated as the header
 * separator (`|---|---|`) and skipped during cell rendering — but if it
 * does NOT match the separator shape (i.e. only-dashes-and-pipes after
 * stripping), the run is treated as plain pipe-prefixed text instead.
 */
type LineBlock = { kind: 'line'; line: string };
type TableBlock = {
  kind: 'table';
  indent: number;
  header: string[];
  rows: string[][];
};
type Block = LineBlock | TableBlock;

const SEPARATOR_RE = /^\|?\s*[-:]+\s*(\|\s*[-:]+\s*)+\|?$/;

function isTableLine(line: string): boolean {
  return line.trimStart().startsWith('|');
}

function isSeparatorLine(line: string): boolean {
  const stripped = line.trim();
  return SEPARATOR_RE.test(stripped);
}

function parseRowCells(line: string): string[] {
  // Strip leading whitespace, then drop the leading and trailing pipe so
  // we don't get empty fragments at the row edges.
  let s = line.trimStart();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  // Split on un-escaped pipes. We use a placeholder swap to handle the
  // ``\|`` escape produced by the scraper.
  const placeholder = '\u0000PIPE\u0000';
  return s
    .replace(/\\\|/g, placeholder)
    .split('|')
    .map((cell) => cell.replace(new RegExp(placeholder, 'g'), '|').trim());
}

export function tokenizeQuote(quote: string): Block[] {
  const lines = quote.split('\n');
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // A table requires at least two consecutive table-shaped lines AND
    // the second to be a separator row.
    if (
      isTableLine(line) &&
      i + 1 < lines.length &&
      isTableLine(lines[i + 1]) &&
      isSeparatorLine(lines[i + 1])
    ) {
      // Capture indent from the header line.
      const indentMatch = line.match(/^( *)/);
      const indent = indentMatch ? indentMatch[1].length : 0;
      const header = parseRowCells(line);
      // Skip header + separator.
      let j = i + 2;
      const rows: string[][] = [];
      while (j < lines.length && isTableLine(lines[j])) {
        // Defensive: don't accidentally swallow a second separator as a
        // body row.
        if (isSeparatorLine(lines[j])) break;
        rows.push(parseRowCells(lines[j]));
        j += 1;
      }
      out.push({ kind: 'table', indent, header, rows });
      i = j;
      continue;
    }
    out.push({ kind: 'line', line });
    i += 1;
  }
  return out;
}

interface Props {
  citation: Citation;
  children?: React.ReactNode;
}

/**
 * Renders an inline citation as a clickable link.
 *
 * - Children (if provided) are the link label — e.g. the inline text
 *   `FAR 6.001(a)` matched by the remark plugin. If no children are
 *   provided, a fallback label derived from the citation itself is used.
 * - Hover → popover with `canonical` and `quote`.
 * - Click → opens `url` in a new tab (RFO only).
 * - RFO vs upload styles via `data-source` attribute + color.
 */
const CitationMarker: React.FC<Props> = ({ citation, children }) => {
  const theme = useTheme();
  const { id, source, canonical, quote, filename, url, section, kind, paragraph, title: sectionTitle } =
    citation;
  const isRfo = source === 'rfo';
  const isLegacy = isLegacyFarCitation(citation);
  // A "regulation" citation is one backed by an acquisition.gov source page
  // (RFO or Legacy FAR), as opposed to an `upload`. Legacy citations carry
  // source `'legacy'` (a real acquisition.gov legacy link), so they must
  // follow the same clickable-link + header path as RFO — only the
  // authority label and the Source page differ (§4.6).
  const isRegulation = isRfo || isLegacy || source === 'policy';
  // Treat absent kind as FAR for backward compat with v1 citations.
  const effectiveKind = kind ?? 'far';
  const hasLink = isRegulation && typeof url === 'string' && url.length > 0;

  // Tooltip header text. For RFO citations, build the header from the
  // regulation prefix + section number + paragraph marker (e.g. ``(a)``,
  // ``(b)(1)``) when present, plus section title for FAR / PGI when
  // available. Defensive fallback: if ``section`` is missing, fall back to
  // ``canonical``. Upload citations always use ``filename``.
  const hasSection = typeof section === 'string' && section.trim().length > 0;
  const hasSectionTitle =
    typeof sectionTitle === 'string' && sectionTitle.trim().length > 0;
  const hasParagraph =
    typeof paragraph === 'string' && paragraph.trim().length > 0;
  const paragraphSuffix = hasParagraph ? paragraph : '';
  // subpart-538.71 supplement sections (e.g. 538.7103-3) arrive with
  // kind === 'far' because their FAR-shaped section ids match the upstream
  // classifier's FAR regex. Branch on the section numbering so they render
  // under the RFO's own "subpart 538.71" source tag (FAR 8.401(b)) — the bare
  // section number tagged with "subpart 538.71 · ", never a wrong "FAR 538.*"
  // header and never an invented "GSAR" acronym. Stays in sync with the
  // backend reference-block header (## subpart 538.71 · 538.7103-3). FAR/RFO
  // citations are unaffected — their section ids never match the pattern.
  const isGsar = isGsarCitation(citation);
  // `FAR` is the persisted canonical key for ordinary RFO sections. It is
  // intentionally retained for matching, links, and storage, but the user
  // facing authority label is `RFO`. Supplements keep their own label and
  // Legacy FAR is distinguished by provenance above.
  const isOrdinaryRfoFar = isRfo && effectiveKind === 'far' && !isGsar;
  let rfoHeaderText: string;
  if (isLegacy && hasSection) {
    // Legacy FAR: mirror the `far` branch but with the "Legacy FAR" prefix
    // so the version is unambiguous in the hover header (\u00a74.6). Provenance
    // comes from the carried kind/source, never the section number.
    rfoHeaderText = hasSectionTitle
      ? `Legacy FAR ${section}${paragraphSuffix} \u2014 ${sectionTitle}`
      : `Legacy FAR ${section}${paragraphSuffix}`;
  } else if (isGsar && hasSection) {
    rfoHeaderText = hasSectionTitle
      ? `${SUPPLEMENT_538_71_LABEL} \u00b7 ${section}${paragraphSuffix} \u2014 ${sectionTitle}`
      : `${SUPPLEMENT_538_71_LABEL} \u00b7 ${section}${paragraphSuffix}`;
  } else if (isOrdinaryRfoFar && hasSection) {
    rfoHeaderText = hasSectionTitle
      ? `RFO ${section}${paragraphSuffix} \u2014 ${sectionTitle}`
      : `RFO ${section}${paragraphSuffix}`;
  } else if (effectiveKind === 'dfars' && hasSection) {
    rfoHeaderText = hasSectionTitle
      ? `DFARS ${section}${paragraphSuffix} \u2014 ${sectionTitle}`
      : `DFARS ${section}${paragraphSuffix}`;
  } else if (effectiveKind === 'pgi' && hasSection) {
    rfoHeaderText = hasSectionTitle
      ? `PGI ${section}${paragraphSuffix} \u2014 ${sectionTitle}`
      : `PGI ${section}${paragraphSuffix}`;
  } else if (effectiveKind === 'memo') {
    rfoHeaderText = canonical ?? `DoD class deviation #${id}`;
  } else {
    rfoHeaderText = canonical ?? `Citation #${id}`;
  }
  const headerText = isRegulation ? rfoHeaderText : filename ?? `Upload #${id}`;

  const handleClick = () => {
    if (hasLink) {
      window.open(url as string, '_blank', 'noopener,noreferrer');
    }
  };

  const rawLabel =
    children !== undefined && children !== null && children !== ''
      ? children
      : canonical ?? filename ?? `#${id}`;
  // ReactMarkdown supplies the matched inline citation as a string. When
  // rendering historic persisted RFO prose (`FAR <section>`), rewrite only
  // that marker label; the citation's canonical FAR key remains untouched.
  const label =
    isOrdinaryRfoFar && typeof rawLabel === 'string'
      ? rawLabel.replace(/^FAR(?=\s+\d+\.\d)/, 'RFO')
      : rawLabel;

  // Tokenize the quote into a mixed list of line-blocks and table-blocks.
  // - Line-blocks render as a `Box` with `paddingLeft: ${leadingSpaces}ch`
  //   (preserves hanging indent for wrapped text).
  // - Table-blocks render as a real `<Table>` (MUI) so the structural
  //   information from the FAR overhaul page survives the round-trip
  //   (header → body, rows → rows) instead of flattening to prose.
  const blocks = tokenizeQuote(quote ?? '');

  const title = (
    <Box data-chat-answer-content sx={{ maxWidth: 640 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: 'primary.main', display: 'block' }}
      >
        {headerText}
      </Typography>
      {citation.source_verification && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          {citation.source_verification}
        </Typography>
      )}
      {typeof url === 'string' && url.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.25,
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Source:{' '}
          <Box
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            // Prevent the parent button's onClick (which opens the URL via
            // window.open) from double-firing when the user clicks the link
            // directly. The anchor itself handles navigation.
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {url}
          </Box>
        </Typography>
      )}
      <Box sx={{ mt: 0.5 }} data-testid="citation-quote">
        {blocks.map((block, idx) => {
          if (block.kind === 'table') {
            return (
              <Box
                key={idx}
                data-citation-table=""
                sx={{
                  pl: `${block.indent}ch`,
                  my: 0.5,
                }}
              >
                <Table
                  size="small"
                  sx={{
                    // Tight, readable defaults — no aggressive padding so
                    // the table fits inside the 640px tooltip.
                    fontSize: '0.75rem',
                    '& .MuiTableCell-root': {
                      px: 0.75,
                      py: 0.25,
                      fontSize: '0.75rem',
                      lineHeight: 1.3,
                      borderColor: 'divider',
                      // Allow long cell content to wrap rather than force
                      // horizontal overflow.
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      verticalAlign: 'top',
                      color: 'text.secondary',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 600,
                      color: 'text.primary',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {block.header.map((cell, ci) => (
                        <TableCell key={ci}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {block.rows.map((row, ri) => (
                      <TableRow key={ri}>
                        {row.map((cell, ci) => (
                          <TableCell key={ci}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          }
          const line = block.line;
          const match = line.match(/^( *)(.*)$/);
          const leadingSpaces = match ? match[1].length : 0;
          const text = match ? match[2] : line;
          const isBlank = text.length === 0;
          return (
            <Box
              key={idx}
              data-indent-ch={leadingSpaces}
              sx={{
                pl: `${leadingSpaces}ch`,
                // Preserve in-line wrapping continuing inside the padding.
                whiteSpace: 'pre-wrap',
                // Long unbreakable tokens (e.g. URLs) should soft-wrap
                // rather than force the tooltip to overflow horizontally.
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                typography: 'caption',
                color: 'text.secondary',
                // Ensure blank/empty lines still take up vertical space so
                // paragraph breaks stay visible.
                minHeight: isBlank ? '1em' : undefined,
              }}
            >
              {isBlank ? '\u00A0' : text}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={title}
      enterDelay={150}
      leaveDelay={0}
      arrow
      placement="left"
      // interactive so the user can scroll within the tooltip.
      disableInteractive={false}
      componentsProps={{
        popper: {
          // The FarChatPopup Paper (zIndex 2050) and the home-chat
          // Dialog (zIndex 2200) both create their own stacking contexts,
          // so the default theme.zIndex.tooltip (1500) can end up behind
          // either. Bump the Popper above both popup containers and the
          // default tooltip zIndex so the tooltip always renders in front.
          sx: {
            zIndex: (theme) =>
              Math.max(theme.zIndex.tooltip, 2200) + 50,
          },
        },
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            boxShadow: 2,
            maxWidth: 640,
            // Long FAR sections (e.g. 6.103-2) can overflow the viewport.
            // Cap the tooltip height and allow the user to scroll through
            // the quote within the tooltip itself.
            maxHeight: '60vh',
            overflowY: 'auto',
            // Suppress horizontal scrollbar — content should soft-wrap
            // within the tooltip's content box instead.
            overflowX: 'hidden',
            p: 1.5,
            fontSize: '0.8125rem',
            // Subtle scrollbar styling so the scroll affordance is visible
            // without overwhelming the small tooltip surface.
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.25),
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '& .MuiTooltip-arrow': {
              color: 'background.paper',
              '&::before': {
                border: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
            },
          },
        },
      }}
    >
      <Box
        component="button"
        type="button"
        role="button"
        data-source={source}
        onClick={handleClick}
        sx={{
          border: 0,
          background: 'transparent',
          p: 0,
          cursor: hasLink ? 'pointer' : 'help',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'inherit',
          color: isRegulation ? 'primary.main' : 'success.main',
          textDecoration: hasLink ? 'underline' : 'none',
          borderRadius: '4px',
          display: 'inline',
          ...(isRegulation
            ? {}
            : {
                backgroundColor: alpha(theme.palette.success.main, 0.12),
                px: 0.5,
                py: '1px',
              }),
          '&:hover': {
            opacity: 0.8,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: isRegulation ? 'primary.light' : 'success.light',
          },
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
};

export default CitationMarker;
